
import { useEffect, useState } from 'react';
import { useParams }           from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon       from '@mui/icons-material/ErrorOutline';

// ===== Match existing unauth URL construction exactly =====
// Same pattern as unauthApi.js:
//   proxyPrefix + baseApi + 'unauth'
const proxyPrefix = process.env.REACT_APP_PROXY_PREFIX      || '';
const baseApi     = process.env.REACT_APP_API_BASE_URL       || '';
const endPoint    = 'unauth';

const unauthBaseUrl = proxyPrefix + baseApi + endPoint;

// Resulting base URLs:
// Local: http://localhost:3000/https://devapi.revfer.com/unauth
// Dev:   https://devapi.revfer.com/unauth
// Prod:  https://api.revfer.com/unauth

const TokenRedirect = ({ action }) => {
  const { token }           = useParams();
  const [status, setStatus] = useState('loading'); // loading | redirecting | error

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }

    // Small delay so user sees the branded spinner
    // rather than an instant blank flash
    const timer = setTimeout(() => {
      const endpoint = action === 'approve'
        ? `${unauthBaseUrl}/v2/drafts/token/approve?token=${token}`
        : `${unauthBaseUrl}/v2/drafts/token/reject?token=${token}`;

      console.log('TokenRedirect — navigating to:', endpoint);
      setStatus('redirecting');
      window.location.href = endpoint;
    }, 500);

    return () => clearTimeout(timer);

  }, [token, action]);

  // ===== Error state — token missing from URL =====
  if (status === 'error') {
    return (
      <Box sx={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '100vh',
        gap:            2,
        p:              3,
        textAlign:      'center',
        bgcolor:        '#F3F9FF',
      }}>
        <Box sx={{
          bgcolor:      'white',
          borderRadius: 4,
          p:            5,
          maxWidth:     400,
          width:        '100%',
          boxShadow:    '0 4px 24px rgba(0,0,0,0.08)',
        }}>
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} color="#0D2B6B" gutterBottom>
            Invalid Link
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            This link is missing required information.
            Please use the full link from your SMS or email message.
          </Typography>
          <Button
            variant="contained"
            href="https://www.revfer.com"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Go to Revfer
          </Button>
        </Box>
      </Box>
    );
  }

  // ===== Loading / redirecting state =====
  const isApprove = action === 'approve';

  return (
    <Box sx={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      minHeight:      '100vh',
      gap:            2,
      bgcolor:        '#F3F9FF',
      p:              3,
    }}>
      <Box sx={{
        bgcolor:      'white',
        borderRadius: 4,
        p:            5,
        maxWidth:     400,
        width:        '100%',
        textAlign:    'center',
        boxShadow:    '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <CircularProgress
          size={48}
          sx={{
            color: isApprove ? 'success.main' : 'error.main',
            mb:    2
          }}
        />
        <Typography variant="h6" fontWeight={700} color="#0D2B6B" gutterBottom>
          {status === 'redirecting'
            ? `${isApprove ? 'Approving' : 'Rejecting'} draft...`
            : 'Processing your request...'
          }
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we process your{' '}
          {isApprove ? 'approval' : 'rejection'}.
        </Typography>
      </Box>
    </Box>
  );
};

export default TokenRedirect;